/**
 * Accès aux données géographiques, toutes fournies par des URL à l'intégration :
 * rien n'est embarqué dans le module.
 *
 * Deux formes de grille :
 *  - un descripteur `grille.json` (format `spanb-grille-tuiles`), qui pointe vers des
 *    tuiles GeoJSON et un index id → tuile : pour les grandes grilles ;
 *  - une FeatureCollection GeoJSON unique : pour les petites grilles.
 */
import { cleTuile, mailleContenant, tuilesCandidates, tuilesEmprise, type EntiteSurface, type GeometrieSurface } from './geometrie'
import type { Maille, SiteResume, SourceSelection } from './types'

export interface EntiteGrille extends EntiteSurface {
  id: number
  geometry: GeometrieSurface
  properties: Record<string, unknown>
}

interface Descripteur {
  format: string
  version_format: string
  nom?: string
  emprise: [number, number, number, number]
  demi_largeur_max: number
  tuiles: { modele: string; taille: number; liste: string[] }
  index?: { modele: string; taille_bloc: number; blocs?: number[] }
}

const cache = new Map<string, Promise<unknown>>()

/** Nom du paramètre d'URL qui porte la clé d'accès aux données. */
export const PARAM_CLE = 'k'

/** L'URL sans la valeur de la clé : pour les messages d'erreur et la console. */
export function masquerCle(url: string): string {
  try {
    const u = new URL(url)
    if (!u.searchParams.has(PARAM_CLE)) return u.href
    u.searchParams.set(PARAM_CLE, 'x')
    return u.href.replace(`${PARAM_CLE}=x`, `${PARAM_CLE}=…`)
  } catch {
    return url
  }
}

/** Le serveur des données a refusé la clé (HTTP 403). */
export class ErreurAcces extends Error {
  constructor(readonly url: string) {
    super(`accès refusé (403) à ${masquerCle(url)} : clé « ${PARAM_CLE} » absente ou invalide`)
    this.name = 'ErreurAcces'
  }
}

/**
 * Ajoute la clé à une URL de données. Uniquement sur le domaine de la grille :
 * une couche ou un fond hébergés ailleurs ne reçoivent jamais la clé.
 */
export function avecCle(url: string, cle: string | null, origine: string | null): string {
  const u = new URL(url, document.baseURI)
  if (!cle || (origine !== null && u.origin !== origine) || u.searchParams.has(PARAM_CLE)) return u.href
  u.searchParams.set(PARAM_CLE, cle)
  return u.href
}

/**
 * Lit un JSON, une seule fois par URL et par page. Sans cookie : les données ne
 * dépendent d'aucune session, et `credentials: 'omit'` évite d'exiger du serveur
 * un CORS avec identifiants.
 */
export function lireJson<T>(url: string): Promise<T> {
  let p = cache.get(url)
  if (!p) {
    p = fetch(url, { mode: 'cors', credentials: 'omit' }).then(
      (r) => {
        if (r.status === 403) throw new ErreurAcces(url)
        if (!r.ok) throw new Error(`HTTP ${r.status} — ${masquerCle(url)}`)
        return r.json()
      },
      // Le navigateur ne dit pas pourquoi une requête croisée échoue : adresse
      // inexistante et en-tête CORS absent donnent la même erreur « Failed to fetch ».
      (e: Error) => {
        throw new Error(`lecture impossible de ${masquerCle(url)} (${e.message}) : vérifier l’adresse, et que le serveur envoie Access-Control-Allow-Origin`)
      },
    )
    p.catch(() => cache.delete(url))
    cache.set(url, p)
  }
  return p as Promise<T>
}

function normaliser(f: { id?: unknown; geometry: GeometrieSurface; properties?: Record<string, unknown> | null }): EntiteGrille | null {
  const id = Number(f.id ?? f.properties?.id)
  if (!Number.isInteger(id) || !f.geometry) return null
  return { id, geometry: f.geometry, properties: f.properties ?? {} }
}

function empriseDe(entites: EntiteGrille[]): [number, number, number, number] {
  const e: [number, number, number, number] = [Infinity, Infinity, -Infinity, -Infinity]
  const visiter = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      e[0] = Math.min(e[0], c[0] as number); e[1] = Math.min(e[1], c[1] as number)
      e[2] = Math.max(e[2], c[0] as number); e[3] = Math.max(e[3], c[1] as number)
    } else if (Array.isArray(c)) c.forEach(visiter)
  }
  entites.forEach((f) => visiter(f.geometry.coordinates))
  return e
}

export class SourceGrille {
  private constructor(
    readonly url: string,
    readonly nom: string | null,
    readonly emprise: [number, number, number, number],
    readonly taille: number,
    readonly marge: number,
    readonly existantes: ReadonlySet<string>,
    private readonly modele: string | null,
    private readonly index: { modele: string; taille_bloc: number; blocs?: number[] } | null,
    private readonly unique: EntiteGrille[] | null,
    /** Clé d'accès, reportée sur toutes les URL de données du domaine de la grille. */
    private readonly cle: string | null,
  ) {}

  /**
   * Ouvre la grille. La clé vient de `cle`, sinon du paramètre `k` de l'URL de la
   * grille : dans les deux cas, elle suit vers les tuiles, l'index et les couches.
   */
  static async ouvrir(url: string, cle: string | null = null): Promise<SourceGrille> {
    const brute = new URL(url, document.baseURI)
    const cleEffective = cle || brute.searchParams.get(PARAM_CLE) || null
    const absolue = avecCle(brute.href, cleEffective, null)
    const d = await lireJson<Record<string, unknown>>(absolue)
    if (d.type === 'FeatureCollection') {
      const entites = ((d.features as unknown[]) ?? []).map((f) => normaliser(f as never)).filter((f): f is EntiteGrille => f !== null)
      if (!entites.length) throw new Error(`aucune maille exploitable dans ${url}`)
      return new SourceGrille(absolue, null, empriseDe(entites), Infinity, 0, new Set(['*']), null, null, entites, cleEffective)
    }
    const desc = d as unknown as Descripteur
    if (desc.format !== 'spanb-grille-tuiles') throw new Error(`format de grille non reconnu (${url})`)
    if (!String(desc.version_format).startsWith('1.')) throw new Error(`version de format non prise en charge : ${desc.version_format}`)
    return new SourceGrille(
      absolue, desc.nom ?? null, desc.emprise, desc.tuiles.taille, desc.demi_largeur_max,
      new Set(desc.tuiles.liste), desc.tuiles.modele, desc.index ?? null, null, cleEffective,
    )
  }

  /** Une URL de données, avec la clé si elle est sur le domaine de la grille. */
  adresse(url: string): string {
    return avecCle(new URL(url, this.url).href, this.cle, new URL(this.url).origin)
  }

  /** Grille découpée en tuiles (sinon : un seul fichier, toujours affiché). */
  get enTuiles(): boolean {
    return this.unique === null
  }

  async tuile(cle: string): Promise<EntiteGrille[]> {
    if (this.unique) return this.unique
    const [x, y] = cle.split('_')
    const url = this.adresse(this.modele!.replace('{x}', x).replace('{y}', y))
    const fc = await lireJson<{ features: unknown[] }>(url)
    return fc.features.map((f) => normaliser(f as never)).filter((f): f is EntiteGrille => f !== null)
  }

  clesEmprise(emprise4326: readonly number[]): string[] {
    return this.unique ? ['*'] : tuilesEmprise(emprise4326, this.existantes, this.taille, this.marge)
  }

  clesPoint(lon: number, lat: number): string[] {
    return this.unique ? ['*'] : tuilesCandidates(lon, lat, this.existantes, this.taille, this.marge)
  }

  async parPoint(lon: number, lat: number): Promise<{ entite: EntiteGrille; cles: string[] } | null> {
    const cles = this.clesPoint(lon, lat)
    const lots = await Promise.all(cles.map((c) => this.tuile(c)))
    const entite = mailleContenant(lots.flat(), lon, lat)
    return entite ? { entite, cles } : null
  }

  /**
   * Retrouve une maille par son id. Avec sa position (lat/lon, que le site connaît
   * par sa table), on lit directement la bonne tuile ; sinon on passe par l'index.
   */
  async parId(id: number, position?: { lat: number; lon: number }): Promise<{ entite: EntiteGrille; cle: string } | null> {
    if (this.unique) {
      const entite = this.unique.find((f) => f.id === id)
      return entite ? { entite, cle: '*' } : null
    }
    let cles: string[] = []
    if (position) cles = this.clesPoint(position.lon, position.lat)
    else if (this.index) {
      const bloc = Math.floor(id / this.index.taille_bloc)
      // Bloc absent de la liste : id hors de la grille, sans requête (qui finirait en 404).
      if (this.index.blocs && !this.index.blocs.includes(bloc)) return null
      const url = this.adresse(this.index.modele.replace('{bloc}', String(bloc)))
      try {
        const table = await lireJson<Record<string, string>>(url)
        if (table[String(id)]) cles = [table[String(id)]]
      } catch (e) {
        if (e instanceof ErreurAcces) throw e   // un refus n'est pas « maille inconnue »
        return null
      }
    }
    for (const cle of cles) {
      const entite = (await this.tuile(cle)).find((f) => f.id === id)
      if (entite) return { entite, cle }
    }
    return null
  }

  /** Clé de la tuile d'une maille, d'après son centroïde (règle de rangement). */
  cleDe(e: EntiteGrille): string {
    if (this.unique) return '*'
    const [lon, lat] = centroideDe(e)
    return cleTuile(lon, lat, this.taille)
  }
}

function nombre(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  return Number.isFinite(n) ? n : null
}

/** Centroïde : propriétés lon/lat si présentes (exactes), sinon moyenne de l'anneau extérieur. */
export function centroideDe(e: EntiteGrille): [number, number] {
  const lon = nombre(e.properties.lon), lat = nombre(e.properties.lat)
  if (lon !== null && lat !== null) return [lon, lat]
  const poly = (e.geometry.type === 'Polygon' ? e.geometry.coordinates : (e.geometry.coordinates as never[])[0]) as number[][][]
  const anneau = poly[0].slice(0, -1)
  return [anneau.reduce((s, p) => s + p[0], 0) / anneau.length, anneau.reduce((s, p) => s + p[1], 0) / anneau.length]
}

function versSite(s: unknown): SiteResume {
  if (s && typeof s === 'object') {
    const o = s as Record<string, unknown>
    const texte = (v: unknown) => (v == null ? null : String(v))
    return { id: (o.id as number | string) ?? null, code: texte(o.code), nom: texte(o.nom ?? o.name), type: texte(o.type ?? o.type_site) }
  }
  return { id: s as number | string, code: null, nom: null, type: null }
}

const r6 = (v: number) => Math.round(v * 1e6) / 1e6

export function versMaille(e: EntiteGrille, source: SourceSelection, point?: [number, number]): Maille {
  const p = e.properties
  const [lon, lat] = centroideDe(e)
  return {
    id: e.id,
    code: p.code == null ? null : String(p.code),
    region: typeof p.region === 'string' ? p.region : null,
    sites: Array.isArray(p.sites) ? p.sites.map(versSite) : [],
    centroide: [r6(lon), r6(lat)],
    proprietes: { ...p },
    source,
    ...(point ? { point: [r6(point[0]), r6(point[1])] as [number, number] } : {}),
  }
}
